/**
 * @architecture-header 1
 * @layer host
 * @domain browser
 * @responsibility Create browser-specific API, external/transient navigation, and demo-mode adapters.
 * @state-owner immutable host configuration; per-user portal audience stamp in localStorage; tab-scoped pending portal handoff and demo sign-out marker in sessionStorage
 * @allowed-imports @vispo/api-client; @vispo/workspace-web environment type
 * @forbidden domain modules; router tree; query wrappers; design system
 * @public-api web main entry only
 * @side-effects browser navigation/history, authenticated HTTP transport, persisted portal audience storage, and tab-scoped pending-handoff/demo-sign-out storage
 * @tests browser-host.test.ts
 * @security-boundary browser host to external network
 * @data-classification access token, user identifier, and portal audience in localStorage; pending portal audience and non-user demo sign-out marker in sessionStorage
 * @trust-model environment values and URLs are untrusted and validated before use
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { createVispoApiClient } from '@vispo/api-client';
import { seatStatusSchema } from '@vispo/contracts';
import type {
  WorkspaceAuthAdapter,
  WorkspaceEnvironment,
  WorkspacePresenceRecord,
  WorkspaceRealtimeAdapter,
  WorkspaceSession,
  WorkspaceTestAdapter,
  WorkspaceTransientNavigationAdapter,
} from '@vispo/workspace-web';
import { WorkspaceAuthError } from '@vispo/workspace-web';
import { clearBrowserTransientState } from './browser-storage';
import type { BrowserRuntimeConfig } from './runtime-config';

const PORTAL_AUDIENCE_PREFIX = 'vispo:iam:portal-audience:';
const PENDING_PORTAL_AUDIENCE_KEY = `${PORTAL_AUDIENCE_PREFIX}pending`;
const DEMO_SIGNED_OUT_KEY = 'vispo:e2e:demo-signed-out';

function portalAudienceKey(userId: string): string {
  return `${PORTAL_AUDIENCE_PREFIX}${userId}`;
}

function portalStorage(): Storage | null {
  return typeof window === 'undefined' ? null : window.localStorage;
}

function pendingPortalStorage(): Storage | null {
  return typeof window === 'undefined' ? null : window.sessionStorage;
}

function demoSessionIsSignedOut(): boolean {
  return pendingPortalStorage()?.getItem(DEMO_SIGNED_OUT_KEY) === 'true';
}

function markDemoSessionSignedOut(): void {
  pendingPortalStorage()?.setItem(DEMO_SIGNED_OUT_KEY, 'true');
}

function clearDemoSessionSignedOut(): void {
  pendingPortalStorage()?.removeItem(DEMO_SIGNED_OUT_KEY);
}

function readPortalAudience(userId: string): WorkspaceSession['portalAudience'] {
  const value = portalStorage()?.getItem(portalAudienceKey(userId));
  return value === 'student' || value === 'teacher' ? value : undefined;
}

function stampPortalAudience(userId: string | null, audience: 'student' | 'teacher') {
  if (userId) {
    portalStorage()?.setItem(portalAudienceKey(userId), audience);
    return;
  }
  pendingPortalStorage()?.setItem(PENDING_PORTAL_AUDIENCE_KEY, audience);
}

function clearPortalAudience(userId: string | null) {
  const storage = portalStorage();
  if (userId) {
    storage?.removeItem(portalAudienceKey(userId));
  } else {
    for (let index = storage?.length ?? 0; index > 0; index -= 1) {
      const key = storage?.key(index - 1);
      if (key?.startsWith(PORTAL_AUDIENCE_PREFIX)) storage?.removeItem(key);
    }
  }
  // Remove a key written by the short-lived localStorage implementation during its migration.
  storage?.removeItem(PENDING_PORTAL_AUDIENCE_KEY);
  pendingPortalStorage()?.removeItem(PENDING_PORTAL_AUDIENCE_KEY);
}

function deriveLinkedProviders(user: Session['user']): readonly ('email' | 'google')[] {
  const raw = Array.isArray(user.app_metadata?.providers)
    ? user.app_metadata.providers
    : typeof user.app_metadata?.provider === 'string'
      ? [user.app_metadata.provider]
      : [];
  return raw.filter(
    (provider): provider is 'email' | 'google' => provider === 'email' || provider === 'google',
  );
}

function projectSession(session: Session | null): WorkspaceSession | null {
  if (!session) return null;
  const pendingAudience = pendingPortalStorage()?.getItem(PENDING_PORTAL_AUDIENCE_KEY);
  if (pendingAudience === 'student' || pendingAudience === 'teacher') {
    stampPortalAudience(session.user.id, pendingAudience);
    pendingPortalStorage()?.removeItem(PENDING_PORTAL_AUDIENCE_KEY);
  }
  const displayName =
    typeof session.user.user_metadata.full_name === 'string'
      ? session.user.user_metadata.full_name
      : typeof session.user.user_metadata.name === 'string'
        ? session.user.user_metadata.name
        : typeof session.user.user_metadata.display_name === 'string'
          ? session.user.user_metadata.display_name
          : null;
  return {
    userId: session.user.id,
    email: session.user.email ?? '',
    displayName,
    avatarUrl:
      typeof session.user.user_metadata.avatar_url === 'string'
        ? session.user.user_metadata.avatar_url
        : typeof session.user.user_metadata.picture === 'string'
          ? session.user.user_metadata.picture
          : null,
    portalAudience: readPortalAudience(session.user.id),
    linkedProviders: deriveLinkedProviders(session.user),
  };
}

function authStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object' || !('status' in error)) return undefined;
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

function authCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('code' in error)) return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

function normalizeRegistrationEmail(email: string): string {
  return email.trim().toLowerCase();
}

function mapSupabaseAuthError(error: unknown): WorkspaceAuthError {
  // The literal is unconfirmed until the first Staging executable check in
  // frontend/apps/web/e2e/staging/auth.spec.ts asserts the provider code.
  // The shared UI receives only this mapped state; it never keys on a provider code or message.
  const isUnknownAccount = authStatus(error) === 422 && authCode(error) === 'otp_disabled';
  return new WorkspaceAuthError(isUnknownAccount ? 'unknown-account' : 'retryable');
}

export function createSupabaseAuthAdapter(client: SupabaseClient): WorkspaceAuthAdapter {
  let lastUserId: string | null = null;
  return {
    async getSession() {
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      const projected = projectSession(data.session);
      lastUserId = projected?.userId ?? lastUserId;
      return projected;
    },
    subscribe(listener) {
      const {
        data: { subscription },
      } = client.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT') clearPortalAudience(lastUserId);
        const projected = projectSession(session);
        lastUserId = projected?.userId ?? lastUserId;
        listener(projected);
      });
      return () => subscription.unsubscribe();
    },
    async sendEmailCode({ email }) {
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
      });
      if (error) throw mapSupabaseAuthError(error);
    },
    async resendEmailCode({ email }) {
      if (await this.getSession()) throw new WorkspaceAuthError('retryable');
      const { error } = await client.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
      });
      if (error) throw mapSupabaseAuthError(error);
    },
    async sendRegistrationCode({ email }) {
      if (await this.getSession()) throw new WorkspaceAuthError('retryable');
      const { error } = await client.auth.signInWithOtp({
        email: normalizeRegistrationEmail(email),
        options: { shouldCreateUser: true },
      });
      if (error) throw mapSupabaseAuthError(error);
    },
    async verifyEmailCode({ email, token }) {
      const { error } = await client.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });
      if (error) throw mapSupabaseAuthError(error);
    },
    async verifyRegistrationCode({ email, token }) {
      if (await this.getSession()) throw new WorkspaceAuthError('retryable');
      const { error } = await client.auth.verifyOtp({
        email: normalizeRegistrationEmail(email),
        token,
        type: 'email',
      });
      if (error) throw mapSupabaseAuthError(error);
    },
    async signInWithGoogle(options) {
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: canonicalAuthRedirectUrl(window.location.origin, options?.redirectPath),
        },
      });
      if (error) throw mapSupabaseAuthError(error);
    },
    async setPortalAudience(audience) {
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      stampPortalAudience(data.session?.user.id ?? null, audience);
    },
    async registerWithGoogle(options) {
      if (await this.getSession()) throw new WorkspaceAuthError('retryable');
      const { error } = await client.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: canonicalAuthRedirectUrl(window.location.origin, options?.redirectPath),
        },
      });
      if (error) throw mapSupabaseAuthError(error);
    },
    async requestEmailChange(newEmail) {
      const { error } = await client.auth.updateUser({ email: newEmail });
      if (error) throw mapSupabaseAuthError(error);
    },
    async signOut() {
      const { data } = await client.auth.getSession();
      // IAM-F08: revoke only this device's session — scope 'local' skips the server-side
      // refresh-token revocation that Supabase's default 'global' scope would apply to every
      // device the user is signed into.
      const { error } = await client.auth.signOut({ scope: 'local' });
      if (error) throw error;
      clearPortalAudience(data.session?.user.id ?? lastUserId);
      lastUserId = null;
      clearBrowserTransientState();
    },
  };
}

export function canonicalAuthRedirectUrl(origin: string, redirectPath = '/'): string {
  const parsed = new URL(origin);
  if (
    parsed.protocol !== 'https:' &&
    !(parsed.protocol === 'http:' && ['127.0.0.1', 'localhost'].includes(parsed.hostname))
  ) {
    throw new Error('Auth redirect origin must use HTTPS or local HTTP');
  }
  if (
    !/^\/(student|teacher)\/login$/.test(redirectPath) &&
    !/^\/join\/[^/]+$/.test(redirectPath) &&
    redirectPath !== '/'
  ) {
    throw new Error('Auth redirect path must be a Vispo audience login path');
  }
  parsed.pathname = redirectPath;
  parsed.search = '';
  parsed.hash = '';
  return parsed.href;
}

function normalizeDemoUser(
  user:
    | {
        userId: string;
        email: string;
        displayName: string;
        avatarUrl?: string | null;
        linkedProviders?: readonly ('email' | 'google')[];
      }
    | null
    | undefined,
): WorkspaceSession | null {
  if (!user) return null;
  return { ...user, linkedProviders: user.linkedProviders ?? ['email'] };
}

function createDemoAuthAdapter(): WorkspaceAuthAdapter {
  const e2e = globalThis as typeof globalThis & {
    __VISPO_AUTH_E2E_ANONYMOUS__?: boolean;
    __VISPO_AUTH_E2E_ERROR__?: 'unknown-account' | 'retryable';
    __VISPO_AUTH_E2E_USER__?: {
      userId: string;
      email: string;
      displayName: string;
      avatarUrl?: string | null;
      linkedProviders?: readonly ('email' | 'google')[];
    } | null;
    __VISPO_AUTH_E2E_RESEND_DEFERRED__?: boolean;
    __VISPO_AUTH_E2E_RESEND_RESOLVE__?: () => void;
  };
  const configuredUser = e2e.__VISPO_AUTH_E2E_USER__;
  let session: WorkspaceSession | null =
    e2e.__VISPO_AUTH_E2E_ANONYMOUS__ ||
    configuredUser === null ||
    (!configuredUser && demoSessionIsSignedOut())
      ? null
      : configuredUser
        ? normalizeDemoUser(configuredUser)
        : {
            userId: 'demo-user',
            email: 'demo@vispo.local',
            displayName: 'Demo User',
            linkedProviders: ['email'],
          };
  const listeners = new Set<(nextSession: WorkspaceSession | null) => void>();
  const publish = () => {
    for (const listener of listeners) listener(session);
  };
  if (typeof globalThis.addEventListener === 'function') {
    globalThis.addEventListener('vispo-auth-e2e-user-changed', () => {
      session = normalizeDemoUser(e2e.__VISPO_AUTH_E2E_USER__);
      if (!session) clearBrowserTransientState();
      publish();
    });
  }
  return {
    getSession: () => Promise.resolve(session),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async sendEmailCode() {
      const error = e2e.__VISPO_AUTH_E2E_ERROR__;
      if (error) throw new WorkspaceAuthError(error);
    },
    async resendEmailCode() {
      const error = e2e.__VISPO_AUTH_E2E_ERROR__;
      if (e2e.__VISPO_AUTH_E2E_RESEND_DEFERRED__) {
        await new Promise<void>((resolve) => {
          e2e.__VISPO_AUTH_E2E_RESEND_RESOLVE__ = resolve;
        });
        delete e2e.__VISPO_AUTH_E2E_RESEND_DEFERRED__;
        delete e2e.__VISPO_AUTH_E2E_RESEND_RESOLVE__;
      }
      if (error) throw new WorkspaceAuthError(error);
    },
    async sendRegistrationCode() {
      const error = e2e.__VISPO_AUTH_E2E_ERROR__;
      if (error) throw new WorkspaceAuthError(error);
    },
    async verifyEmailCode({ email }) {
      clearDemoSessionSignedOut();
      session = {
        userId: 'demo-user',
        email,
        displayName: 'Demo User',
        linkedProviders: ['email'],
      };
      publish();
    },
    async verifyRegistrationCode({ email }) {
      clearDemoSessionSignedOut();
      session = {
        userId: 'demo-user',
        email,
        displayName: 'Demo User',
        linkedProviders: ['email'],
      };
      publish();
    },
    async signInWithGoogle() {
      clearDemoSessionSignedOut();
      session = {
        userId: 'demo-user',
        email: 'demo@vispo.local',
        displayName: 'Demo User',
        linkedProviders: ['email', 'google'],
      };
      publish();
    },
    async registerWithGoogle() {
      clearDemoSessionSignedOut();
      session = {
        userId: 'demo-user',
        email: 'demo@vispo.local',
        displayName: 'Demo User',
        linkedProviders: ['email', 'google'],
      };
      publish();
    },
    async setPortalAudience(audience) {
      stampPortalAudience(session?.userId ?? null, audience);
      if (session) {
        session = { ...session, portalAudience: audience };
        publish();
      }
    },
    async requestEmailChange() {
      // Mirrors Supabase's real "secure email change" behavior: the session's email does
      // not update until the provider confirms it, so demo mode leaves `session` untouched.
      const error = e2e.__VISPO_AUTH_E2E_ERROR__;
      if (error) throw new WorkspaceAuthError(error);
    },
    async signOut() {
      // IAM-F08: lets e2e set window.__VISPO_E2E__.setAuthError(...) to prove the account-menu
      // failure path without tearing down demo session state, mirroring every other adapter
      // method's __VISPO_AUTH_E2E_ERROR__ check above.
      const error = e2e.__VISPO_AUTH_E2E_ERROR__;
      if (error) throw new WorkspaceAuthError(error);
      clearPortalAudience(session?.userId ?? null);
      session = null;
      clearBrowserTransientState();
      markDemoSessionSignedOut();
      publish();
    },
  };
}

function projectPresence(value: unknown): WorkspacePresenceRecord | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (
    typeof row.userId !== 'string' ||
    typeof row.studentName !== 'string' ||
    !seatStatusSchema.safeParse(row.status).success ||
    typeof row.lastActiveAt !== 'string'
  ) {
    return null;
  }
  const confusionAlerts = Array.isArray(row.confusionAlerts)
    ? row.confusionAlerts.flatMap(
        (alert): NonNullable<WorkspacePresenceRecord['confusionAlerts']> => {
          if (!alert || typeof alert !== 'object') return [];
          const item = alert as Record<string, unknown>;
          if (
            !['stuck', 'modified', 'manual'].includes(String(item.type)) ||
            typeof item.questionIndex !== 'number' ||
            typeof item.timestamp !== 'string'
          ) {
            return [];
          }
          return [
            {
              type: item.type as 'stuck' | 'modified' | 'manual',
              questionIndex: item.questionIndex,
              timestamp: item.timestamp,
            },
          ];
        },
      )
    : undefined;
  return {
    userId: row.userId,
    studentName: row.studentName,
    status: row.status as WorkspacePresenceRecord['status'],
    lastActiveAt: row.lastActiveAt,
    ...(typeof row.examId === 'string' ? { examId: row.examId } : {}),
    ...(typeof row.examTitle === 'string' ? { examTitle: row.examTitle } : {}),
    ...(typeof row.phase === 'string' ? { phase: row.phase } : {}),
    ...(typeof row.currentQuestionIndex === 'number'
      ? { currentQuestionIndex: row.currentQuestionIndex }
      : {}),
    ...(typeof row.totalQuestions === 'number' ? { totalQuestions: row.totalQuestions } : {}),
    ...(confusionAlerts ? { confusionAlerts } : {}),
  };
}

function createRealtimeAdapter(client: SupabaseClient | null): WorkspaceRealtimeAdapter {
  return {
    async subscribeClassPresence(classId, listener) {
      if (!client) {
        listener({ connected: false, records: [] });
        return () => undefined;
      }
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      if (data.session?.access_token) await client.realtime.setAuth(data.session.access_token);
      const channel = client.channel(`class_presence:${classId}`, {
        config: { presence: { key: `listener_${data.session?.user.id ?? crypto.randomUUID()}` } },
      });
      const publish = (connected: boolean) => {
        const records = Object.values(channel.presenceState())
          .flat()
          .flatMap((entry) => {
            const record = projectPresence(entry);
            return record ? [record] : [];
          });
        listener({ connected, records });
      };
      channel
        .on('presence', { event: 'sync' }, () => publish(true))
        .subscribe((status) => publish(status === 'SUBSCRIBED'));
      return () => {
        void channel.untrack();
        void client.removeChannel(channel);
      };
    },
    async trackClassPresence(classId, record) {
      if (!client) return () => undefined;
      const { data, error } = await client.auth.getSession();
      if (error) throw error;
      if (data.session?.access_token) await client.realtime.setAuth(data.session.access_token);
      const channel = client.channel(`class_presence:${classId}`, {
        config: { presence: { key: `student_${record.userId}` } },
      });
      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') void channel.track(record);
      });
      return () => {
        void channel.untrack();
        void client.removeChannel(channel);
      };
    },
  };
}

function createBrowserTransientNavigationAdapter(): WorkspaceTransientNavigationAdapter {
  let nextEntry = 0;
  return {
    open(key, onBack) {
      const entry = `vispo:${key}:${++nextEntry}`;
      let active = true;
      window.history.pushState({ ...window.history.state, vispoTransientEntry: entry }, '');
      const handleBack = () => {
        active = false;
        onBack();
      };
      window.addEventListener('popstate', handleBack);
      return () => {
        window.removeEventListener('popstate', handleBack);
        queueMicrotask(() => {
          if (active && window.history.state?.vispoTransientEntry === entry) {
            active = false;
            window.history.back();
          }
        });
      };
    },
  };
}

export function createBrowserEnvironment(
  config: BrowserRuntimeConfig,
  testAdapter?: WorkspaceTestAdapter,
): WorkspaceEnvironment {
  const configuredBaseUrl = config.apiUrl.trim();
  const configuredSupabaseUrl = config.supabaseUrl.trim();
  const configuredSupabasePublishableKey = config.supabasePublishableKey.trim();
  const demoMode = config.demoMode;
  const enforcePortalAudience =
    demoMode &&
    (globalThis as typeof globalThis & { __VISPO_AUTH_E2E_ENFORCE_PORTAL_AUDIENCE__?: boolean })
      .__VISPO_AUTH_E2E_ENFORCE_PORTAL_AUDIENCE__ === true;
  const supabase = demoMode
    ? null
    : createClient(configuredSupabaseUrl, configuredSupabasePublishableKey, {
        auth: {
          autoRefreshToken: true,
          detectSessionInUrl: true,
          persistSession: true,
        },
      });
  const auth = supabase ? createSupabaseAuthAdapter(supabase) : createDemoAuthAdapter();

  return {
    apiClient: createVispoApiClient({
      baseUrl: configuredBaseUrl || 'http://127.0.0.1:8080',
      getAccessToken: async () => {
        if (!supabase) return null;
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        return data.session?.access_token ?? null;
      },
      refreshAccessToken: async () => {
        if (!supabase) return null;
        const { data, error } = await supabase.auth.refreshSession();
        if (error) return null;
        return data.session?.access_token ?? null;
      },
    }),
    auth,
    realtime: createRealtimeAdapter(supabase),
    transientNavigation: createBrowserTransientNavigationAdapter(),
    ...(testAdapter ? { testAdapter } : {}),
    platform: 'browser',
    demoMode,
    ...(enforcePortalAudience ? { enforcePortalAudience: true } : {}),
    async openExternal(url) {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:') throw new Error('Only HTTPS links can be opened');
      window.open(parsed.href, '_blank', 'noopener,noreferrer');
    },
  };
}
